
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { api } from '@/lib/api';
import { showToast } from '@/lib/utils/toast';
import { SharedPoll } from '@/lib/shared-types';

interface PollResults {
  poll: SharedPoll;
  totalVotes: number;
  userVote?: number;
}

// API Response Types
interface CreatePollResponse {
  success: boolean;
  data: { poll: SharedPoll };
}

interface GetPollsResponse {
  success: boolean;
  data: { polls: SharedPoll[] };
}

interface GetPollResponse {
  success: boolean;
  data: { poll: SharedPoll };
}

interface GetPollResultsResponse {
  success: boolean;
  data: { results: PollResults };
}

interface VoteResponse {
  success: boolean;
  data: {
    pollId: number;
    optionIndex: number;
    optionVotes: number | Record<string, number>;
    totalVotes: number;
    userVoteIndex: number | null;
    results?: Record<string, number>;
  };
}

interface ApiError {
  success: false;
  error: { code: string; message: string };
}

// API Helper Functions
// SECURITY: Token is now stored in httpOnly cookie and automatically sent by fetch()
// The centralized API client handles auth via request interceptors

const handleApiResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = (await response.json().catch(() => ({ message: 'API Error' }))) as { message?: string };
    throw new Error(error.message || 'API request failed');
  }
  return response.json();
};

const apiCreatePoll = async (streamId: number, question: string, options: string[], endsAt?: string): Promise<SharedPoll> => {
  const response = await api.post<{ poll: SharedPoll }>('polls', {
    streamId,
    question,
    options,
    endsAt,
  });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to create poll');
  }
  if (!response.data?.poll) {
    throw new Error('Invalid response: no poll data');
  }
  return response.data.poll;
};

const apiGetPolls = async (streamId: number): Promise<SharedPoll[]> => {
  const response = await api.get<{ polls: SharedPoll[] }>(`polls?streamId=${streamId}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch polls');
  }
  if (!response.data?.polls) {
    throw new Error('Invalid response: no polls data');
  }
  return response.data.polls;
};

const apiGetPoll = async (pollId: number): Promise<SharedPoll> => {
  const response = await api.get<{ poll: SharedPoll }>(`polls/${pollId}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch poll');
  }
  if (!response.data?.poll) {
    throw new Error('Invalid response: no poll data');
  }
  return response.data.poll;
};

const apiUpdatePoll = async (
  pollId: number,
  updates: {
    question?: string;
    options?: string[];
    endsAt?: string;
    isActive?: boolean;
  }
): Promise<SharedPoll> => {
  const response = await api.put<{ poll: SharedPoll }>(`api/polls/${pollId}`, updates);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to update poll');
  }
  if (!response.data?.poll) {
    throw new Error('Invalid response: no poll data');
  }
  return response.data.poll;
};

const apiVotePoll = async (pollId: number, optionIndex: number): Promise<VoteResponse['data']> => {
  const response = await api.post<VoteResponse['data']>(`api/polls/${pollId}/vote`, { optionIndex });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to vote on poll');
  }
  return response.data as VoteResponse['data'];
};

const apiEndPoll = async (pollId: number): Promise<{ poll: SharedPoll }> => {
  const response = await api.post<{ poll: SharedPoll }>(`api/polls/${pollId}/end`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to end poll');
  }
  if (!response.data?.poll) {
    throw new Error('Invalid response: no poll data');
  }
  return response.data as { poll: SharedPoll };
};

const apiDeletePoll = async (pollId: number): Promise<void> => {
  const response = await api.delete(`api/polls/${pollId}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete poll');
  }
};

const apiRemoveVote = async (pollId: number): Promise<VoteResponse['data']> => {
  const response = await api.delete<VoteResponse['data']>(`api/polls/${pollId}/vote`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to remove vote');
  }
  return response.data as VoteResponse['data'];
};

// Conversion functions between backend and frontend formats
const backendToFrontendPoll = (backendPoll: SharedPoll): Poll => {
  const totalVotes = Object.values(backendPoll.results).reduce((sum, votes) => sum + votes, 0);

  return {
    id: backendPoll.id.toString(),
    streamId: backendPoll.streamId.toString(),
    question: backendPoll.question,
    options: backendPoll.options.map((option, index) => ({
      id: `opt_${backendPoll.id}_${index}`,
      text: option.text,
      votes: backendPoll.results[index.toString()] || 0,
      percentage: totalVotes > 0 ? ((backendPoll.results[index.toString()] || 0) / totalVotes) * 100 : 0,
    })),
    status: backendPoll.isActive ? 'active' : 'ended',
    isAnonymous: false, // Backend doesn't track this yet
    allowMultiple: false, // Backend doesn't track this yet
    totalVotes,
    createdBy: backendPoll.createdBy.toString(),
    createdAt: backendPoll.createdAt,
    startedAt: backendPoll.createdAt, // Assuming started when created
    endedAt: backendPoll.endsAt,
    duration: backendPoll.endsAt ? Math.floor((new Date(backendPoll.endsAt).getTime() - new Date(backendPoll.createdAt).getTime()) / 1000) : undefined,
  };
};

const frontendToBackendOptions = (options: string[]): string[] => {
  return options;
};

const recalculatePollPercentages = (poll: Poll, optionVotes: number[]): Poll => {
  const totalVotes = optionVotes.reduce((sum, votes) => sum + votes, 0);
  return {
    ...poll,
    options: poll.options.map((option, index) => ({
      ...option,
      votes: optionVotes[index] ?? option.votes,
      percentage: totalVotes > 0 ? ((optionVotes[index] ?? option.votes) / totalVotes) * 100 : 0,
    })),
    totalVotes,
  };
};

const updatePollOptionVotes = (
  poll: Poll,
  optionIndex: number,
  votes: number,
  totalVotes?: number
): Poll => {
  const nextOptionVotes = poll.options.map((option, index) =>
    index === optionIndex ? votes : option.votes
  );
  const nextTotalVotes = totalVotes ?? nextOptionVotes.reduce((sum, value) => sum + value, 0);
  return {
    ...poll,
    options: poll.options.map((option, index) => ({
      ...option,
      votes: nextOptionVotes[index],
      percentage: nextTotalVotes > 0 ? (nextOptionVotes[index] / nextTotalVotes) * 100 : 0,
    })),
    totalVotes: nextTotalVotes,
  };
};

const applyPollVoteDelta = (poll: Poll, optionIndex: number, delta: number): Poll => {
  const nextOptionVotes = poll.options.map((option, index) =>
    index === optionIndex ? Math.max(0, option.votes + delta) : option.votes
  );
  return recalculatePollPercentages(poll, nextOptionVotes);
};

// Types for polling system
export interface PollOption {
  id: string;
  text: string;
  votes: number;
  percentage: number;
}

export interface Poll {
  id: string;
  streamId: string;
  question: string;
  options: PollOption[];
  status: 'draft' | 'active' | 'ended';
  isAnonymous: boolean;
  allowMultiple: boolean;
  totalVotes: number;
  createdBy: string;
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
  duration?: number; // in seconds
}

export interface PollVote {
  pollId: string;
  userId: string;
  optionIds: string[]; // For multiple choice polls
  votedAt: string;
}

interface PollState {
  // Current polls
  polls: Poll[];
  userVotes: Map<string, PollVote>; // pollId -> vote
  creatingPoll: boolean;
  votingPollIds: Set<string>;
  endingPollIds: Set<string>;
  deletingPollIds: Set<string>;

  // UI state
  isLoading: boolean;
  error: string | null;
  selectedPoll: Poll | null;

  // Real-time updates
  lastUpdate: string | null;
}

interface PollActions {
  // Poll management
  createPoll: (streamId: string, question: string, options: string[], settings: {
    isAnonymous: boolean;
    allowMultiple: boolean;
    duration?: number;
  }) => Promise<void>;

  updatePoll: (pollId: string, updates: Partial<Poll>) => Promise<void>;
  deletePoll: (pollId: string) => Promise<void>;

  // Poll lifecycle
  startPoll: (pollId: string) => Promise<void>;
  endPoll: (pollId: string) => Promise<void>;

  // Voting
  votePoll: (pollId: string, optionIds: string[]) => Promise<void>;
  removeVote: (pollId: string) => Promise<void>;

  // Bulk operations
  bulkDeletePolls: (pollIds: string[]) => Promise<void>;

  // Data fetching
  loadPolls: (streamId: string, force?: boolean) => Promise<void>;
  loadPoll: (pollId: string) => Promise<void>;

  // UI actions
  selectPoll: (poll: Poll | null) => void;
  clearError: () => void;

  // Real-time updates
  handleRealtimePollUpdate: (poll: Poll) => void;
  handleRealtimeVoteUpdate: (pollId: string, optionIndex: number, votes: number) => void;
}

type PollStore = PollState & PollActions;

export const usePollStore = create<PollStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        polls: [],
        userVotes: new Map(),
        creatingPoll: false,
        votingPollIds: new Set(),
        endingPollIds: new Set(),
        deletingPollIds: new Set(),
        isLoading: false,
        error: null,
        selectedPoll: null,
        lastUpdate: null,

        // Poll management
        createPoll: async (streamId, question, options, settings) => {
          set({ creatingPoll: true, error: null });

          try {
            // Basic validation
            if (!question.trim()) {
              throw new Error('Poll question is required');
            }

            if (options.length < 2) {
              throw new Error('Poll must have at least 2 options');
            }

            if (options.some(opt => !opt.trim())) {
              throw new Error('All poll options must have text');
            }

            // Call API to create poll
            const backendPoll = await apiCreatePoll(
              parseInt(streamId),
              question.trim(),
              options.map(opt => opt.trim()),
              settings.duration ? new Date(Date.now() + settings.duration * 1000).toISOString() : undefined
            );

            // Convert to frontend format and add to state
            const newPoll = backendToFrontendPoll(backendPoll);

            set((state) => ({
              polls: [newPoll, ...state.polls],
              // Keep the current selection unchanged so creating a poll does not
              // unexpectedly steal focus away from an already open detail panel.
              creatingPoll: false,
            }));

            showToast.success('Poll created successfully');
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create poll';
            set({ error: message, creatingPoll: false });
            showToast.error(message);
          }
        },

        updatePoll: async (pollId, updates) => {
          set({ isLoading: true, error: null });

          try {
            const updatePayload: {
              question?: string;
              options?: string[];
              endsAt?: string;
              isActive?: boolean;
            } = {};

            if (updates.question !== undefined) {
              updatePayload.question = updates.question;
            }

            if (updates.options !== undefined) {
              updatePayload.options = updates.options.map((option) => option.text);
            }

            if (updates.endedAt !== undefined) {
              updatePayload.endsAt = updates.endedAt;
            } else if (updates.duration !== undefined) {
              updatePayload.endsAt = new Date(Date.now() + updates.duration * 1000).toISOString();
            }

            if (updates.status !== undefined) {
              updatePayload.isActive = updates.status !== 'ended';
            }

            const backendPoll = await apiUpdatePoll(parseInt(pollId, 10), updatePayload);
            const frontendPoll = backendToFrontendPoll(backendPoll);

            set((state) => ({
              polls: state.polls.map((poll) => (poll.id === pollId ? frontendPoll : poll)),
              selectedPoll: state.selectedPoll?.id === pollId ? frontendPoll : state.selectedPoll,
              isLoading: false
            }));

            showToast.success('Poll updated successfully');
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update poll';
            set({ error: message, isLoading: false });
            showToast.error(message);
          }
        },

        deletePoll: async (pollId) => {
          set((state) => ({
            error: null,
            deletingPollIds: new Set([...state.deletingPollIds, pollId]),
          }));

          try {
            await apiDeletePoll(parseInt(pollId));

            set((state) => ({
              polls: state.polls.filter(poll => poll.id !== pollId),
              selectedPoll: state.selectedPoll?.id === pollId ? null : state.selectedPoll,
              deletingPollIds: new Set([...state.deletingPollIds].filter((id) => id !== pollId)),
            }));

            set((state) => {
              const newVotes = new Map(state.userVotes);
              newVotes.delete(pollId);
              return { userVotes: newVotes };
            });

            showToast.success('Poll deleted successfully');
          } catch (error) {
            set((state) => ({
              error: 'Failed to delete poll',
              deletingPollIds: new Set([...state.deletingPollIds].filter((id) => id !== pollId)),
            }));
            showToast.error('Failed to delete poll');
          }
        },

        // Poll lifecycle
        startPoll: async (pollId) => {
          // Polls are active by default upon creation
          showToast.info('Poll is already active');
        },

        endPoll: async (pollId) => {
          set((state) => ({
            error: null,
            endingPollIds: new Set([...state.endingPollIds, pollId]),
          }));

          try {
            const response = await apiEndPoll(parseInt(pollId));
            const frontendPoll = backendToFrontendPoll(response.poll);

            set((state) => ({
              polls: state.polls.map(poll =>
                poll.id === pollId ? frontendPoll : poll
              ),
              selectedPoll: state.selectedPoll?.id === pollId ? frontendPoll : state.selectedPoll,
              endingPollIds: new Set([...state.endingPollIds].filter((id) => id !== pollId)),
            }));

            showToast.success('Poll ended successfully');
          } catch (error) {
            set((state) => ({
              error: 'Failed to end poll',
              endingPollIds: new Set([...state.endingPollIds].filter((id) => id !== pollId)),
            }));
            showToast.error('Failed to end poll');
          }
        },

        // Voting
        votePoll: async (pollId, optionIds) => {
          const { userVotes } = get();

          if (userVotes.has(pollId)) {
            throw new Error('You have already voted on this poll');
          }

          const previousPoll = get().polls.find((poll) => poll.id === pollId);
          if (!previousPoll) return;
          const previousSelectedPoll = get().selectedPoll;
          const previousVote = get().userVotes.get(pollId);
          const optionIndex = parseInt(optionIds[0].split('_').pop() || '0');
          const vote: PollVote = {
            pollId,
            userId: 'current_user',
            optionIds,
            votedAt: new Date().toISOString(),
          };

          set((state) => ({
            error: null,
            votingPollIds: new Set([...state.votingPollIds, pollId]),
            polls: state.polls.map((poll) =>
              poll.id === pollId ? applyPollVoteDelta(poll, optionIndex, 1) : poll
            ),
            selectedPoll: state.selectedPoll?.id === pollId
              ? applyPollVoteDelta(state.selectedPoll!, optionIndex, 1)
              : state.selectedPoll,
            userVotes: new Map([...state.userVotes, [pollId, vote]]),
          }));

          try {
            const response = await apiVotePoll(parseInt(pollId, 10), optionIndex);
            const selectedVotes = typeof response.optionVotes === 'number'
              ? response.optionVotes
              : response.optionVotes?.[response.optionIndex.toString()] ?? 0;
            set((state) => ({
              polls: state.polls.map((poll) =>
                poll.id === pollId
                  ? updatePollOptionVotes(poll, response.optionIndex, selectedVotes, response.totalVotes)
                  : poll
              ),
              selectedPoll: state.selectedPoll?.id === pollId
                ? updatePollOptionVotes(state.selectedPoll!, response.optionIndex, selectedVotes, response.totalVotes)
                : state.selectedPoll,
              votingPollIds: new Set([...state.votingPollIds].filter((id) => id !== pollId)),
            }));

          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to vote';
            set((state) => ({
              error: message,
              polls: state.polls.map((poll) => (poll.id === pollId ? previousPoll : poll)),
              selectedPoll: previousSelectedPoll?.id === pollId ? previousSelectedPoll : state.selectedPoll,
              userVotes: previousVote
                ? new Map([...state.userVotes, [pollId, previousVote]])
                : new Map([...state.userVotes].filter(([id]) => id !== pollId)),
              votingPollIds: new Set([...state.votingPollIds].filter((id) => id !== pollId)),
            }));
            showToast.error(message);
          }
        },

        removeVote: async (pollId) => {
          const previousPoll = get().polls.find((poll) => poll.id === pollId);
          if (!previousPoll) return;
          const previousSelectedPoll = get().selectedPoll;
          const previousVote = get().userVotes.get(pollId);
          if (!previousVote) return;

          set((state) => ({
            error: null,
            votingPollIds: new Set([...state.votingPollIds, pollId]),
            polls: state.polls.map((poll) => {
              if (poll.id !== pollId) return poll;
              const optionIndex = previousVote.optionIds[0]?.split('_').pop();
              const selectedIndex = optionIndex ? parseInt(optionIndex, 10) : -1;
              return applyPollVoteDelta(poll, selectedIndex, -1);
            }),
            selectedPoll: state.selectedPoll?.id === pollId
              ? (() => {
                  const optionIndex = previousVote.optionIds[0]?.split('_').pop();
                  const selectedIndex = optionIndex ? parseInt(optionIndex, 10) : -1;
                  return applyPollVoteDelta(state.selectedPoll!, selectedIndex, -1);
                })()
              : state.selectedPoll,
            userVotes: new Map([...state.userVotes].filter(([id]) => id !== pollId)),
          }));

          try {
            const response = await apiRemoveVote(parseInt(pollId, 10));
            const responseOptionIndex = response.optionIndex ?? parseInt(previousVote.optionIds[0]?.split('_').pop() || '0', 10);
            const selectedVotes = typeof response.optionVotes === 'number'
              ? response.optionVotes
              : response.optionVotes?.[responseOptionIndex.toString()] ?? 0;

            set((state) => {
              const newVotes = new Map(state.userVotes);
              newVotes.delete(pollId);

              return {
                polls: state.polls.map((poll) =>
                  poll.id === pollId
                    ? updatePollOptionVotes(poll, responseOptionIndex, selectedVotes, response.totalVotes)
                    : poll
                ),
                selectedPoll: state.selectedPoll?.id === pollId
                  ? updatePollOptionVotes(state.selectedPoll!, responseOptionIndex, selectedVotes, response.totalVotes)
                  : state.selectedPoll,
                userVotes: newVotes,
                votingPollIds: new Set([...state.votingPollIds].filter((id) => id !== pollId)),
              };
            });

          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to remove vote';
            set((state) => ({
              error: message,
              polls: state.polls.map((poll) => (poll.id === pollId ? previousPoll : poll)),
              selectedPoll: previousSelectedPoll?.id === pollId ? previousSelectedPoll : state.selectedPoll,
              userVotes: previousVote
                ? new Map([...state.userVotes, [pollId, previousVote]])
                : new Map([...state.userVotes].filter(([id]) => id !== pollId)),
              votingPollIds: new Set([...state.votingPollIds].filter((id) => id !== pollId)),
            }));
            showToast.error(message);
          }
        },

        // Bulk operations
        bulkDeletePolls: async (pollIds) => {
          set({ isLoading: true, error: null });

          try {
            await Promise.all(
              pollIds.map((pollId) => apiDeletePoll(parseInt(pollId, 10)))
            );

            set((state) => ({
              polls: state.polls.filter(poll => !pollIds.includes(poll.id)),
              selectedPoll: state.selectedPoll && pollIds.includes(state.selectedPoll.id)
                ? null
                : state.selectedPoll,
              userVotes: new Map(
                [...state.userVotes].filter(([pollId]) => !pollIds.includes(pollId))
              ),
              isLoading: false
            }));

            showToast.success(`${pollIds.length} polls deleted successfully`);
          } catch (error) {
            set({ error: 'Failed to delete polls', isLoading: false });
            showToast.error('Failed to delete polls');
          }
        },

        // Data fetching
        loadPolls: async (streamId, force = false) => {
          const { polls } = get();

          if (!force && polls.length > 0 && polls[0].streamId === streamId) {
            return; // Already loaded
          }

          set({ isLoading: true, error: null });

          try {
            // Call API to get polls
            const backendPolls = await apiGetPolls(parseInt(streamId));

            // Convert to frontend format
            const frontendPolls = backendPolls.map(backendToFrontendPoll);

            set({
              polls: frontendPolls,
              isLoading: false,
            });
          } catch (error) {
            set({ error: 'Failed to load polls', isLoading: false });
            showToast.error('Failed to load polls');
          }
        },

        loadPoll: async (pollId) => {
          set({ isLoading: true, error: null });

          try {
            const backendPoll = await apiGetPoll(parseInt(pollId));
            const poll = backendToFrontendPoll(backendPoll);

            set((state) => ({
              polls: state.polls.some((existing) => existing.id === pollId)
                ? state.polls.map((existing) => existing.id === pollId ? poll : existing)
                : [poll, ...state.polls],
              selectedPoll: state.selectedPoll?.id === pollId ? poll : state.selectedPoll,
              isLoading: false,
            }));
          } catch (error) {
            set({ error: 'Failed to load poll', isLoading: false });
            showToast.error('Failed to load poll');
          }
        },

        // UI actions
        selectPoll: (poll) => set({ selectedPoll: poll }),
        clearError: () => set({ error: null }),

        // Real-time updates
        handleRealtimePollUpdate: (poll) => {
          set((state) => ({
            polls: state.polls.some((existing) => existing.id === poll.id)
              ? state.polls.map((existing) => (existing.id === poll.id ? poll : existing))
              : [poll, ...state.polls],
            selectedPoll: state.selectedPoll?.id === poll.id ? poll : state.selectedPoll,
            lastUpdate: new Date().toISOString(),
          }));
        },

        handleRealtimeVoteUpdate: (pollId, optionIndex, votes) => {
          set((state) => ({
            polls: state.polls.map(poll => {
              if (poll.id === pollId) {
                const updatedOptions = poll.options.map(option =>
                  option.id === `opt_${pollId}_${optionIndex}` ? { ...option, votes } : option
                );

                // Recalculate percentages
                const totalVotes = updatedOptions.reduce((sum, opt) => sum + opt.votes, 0);
                const optionsWithPercentages = updatedOptions.map(option => ({
                  ...option,
                  percentage: totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0,
                }));

                return {
                  ...poll,
                  options: optionsWithPercentages,
                  totalVotes,
                };
              }
              return poll;
            }),
            selectedPoll: state.selectedPoll?.id === pollId
              ? (() => {
                  const selected = state.selectedPoll!;
                  const updatedOptions = selected.options.map(option =>
                    option.id === `opt_${pollId}_${optionIndex}` ? { ...option, votes } : option
                  );
                  const totalVotes = updatedOptions.reduce((sum, opt) => sum + opt.votes, 0);
                  const optionsWithPercentages = updatedOptions.map(option => ({
                    ...option,
                    percentage: totalVotes > 0 ? (option.votes / totalVotes) * 100 : 0,
                  }));

                  return {
                    ...selected,
                    options: optionsWithPercentages,
                    totalVotes,
                  };
                })()
              : state.selectedPoll,
            lastUpdate: new Date().toISOString(),
          }));
        },
      }),
      {
        name: 'poll-store',
        partialize: (state) => ({
          userVotes: state.userVotes,
        }),
      }
    ),
    {
      name: 'poll-store',
    }
  )
);

// Selectors
export const usePolls = (streamId?: string) => {
  const { polls } = usePollStore();

  if (streamId) {
    return polls.filter(poll => poll.streamId === streamId);
  }

  return polls;
};

export const useActivePolls = (streamId?: string) => {
  const polls = usePolls(streamId);
  return polls.filter(poll => poll.status === 'active');
};

export const usePollStats = (streamId?: string) => {
  const polls = usePolls(streamId);

  return {
    totalPolls: polls.length,
    activePolls: polls.filter(p => p.status === 'active').length,
    endedPolls: polls.filter(p => p.status === 'ended').length,
    draftPolls: polls.filter(p => p.status === 'draft').length,
    totalVotes: polls.reduce((sum, poll) => sum + poll.totalVotes, 0),
    averageVotesPerPoll: polls.length > 0
      ? Math.round(polls.reduce((sum, poll) => sum + poll.totalVotes, 0) / polls.length)
      : 0,
  };
};

export const useUserVote = (pollId: string) => {
  const { userVotes } = usePollStore();
  return userVotes.get(pollId);
};
